import { deleteComment as serverDeleteComment, updateComment as serverUpdateComment, createComment as serverCreateComment, listCommentsByResource as serverFetchComments, bulkListCommentsByResource as serverBulkFetchComments, getCommentThread as serverFetchCommentThread, getBulkComments as serverGetBulkComments, removeReaction as serverRemoveReaction, addReaction as serverAddReaction, markComment as serverMarkComment, unmarkComment as serverUnmarkComment, listCommentContentRevisions as serverFetchCommentHistory, } from '@wix/ambassador-comments-middleware-v1-enriched-comment/http';
import { SortOrder, } from '@wix/ambassador-comments-middleware-v1-enriched-comment/types';
import { fromServerBulkFetchComments, fromServerFetchComments, fromServerFetchCommentThread, fromServerGetBulkComments, toServerRatingAction, fromServerFetchCommentHistory, toServerComment, fromServerComment, } from './comments-service-mappers';
import { withDevTools } from '../../utils/with-dev-tools';
import { wrapServiceWithResult } from '../../utils/wrap-service-with-result';
import { wrapWithFedopsLogging } from '../../utils/wrap-with-fedops-logging';
import { assertFields } from '../../../ts-utils';
import { getCommentCreateError, normalizeUnknownExceptionToError, } from '../../../common/store/comments/comments-errors';
import { fromServerVote } from '../vote-service';
export function createCommentsService({ httpClient, getCrudDevState, getFetchDevState, onError, mocks, fedopsLogger, persistDraft, }) {
    const withFedops = wrapWithFedopsLogging(fedopsLogger);
    const service = (() => {
        const fetchCalls = {
            fetchComments: withFedops(fetchComments(httpClient, mocks?.fetchComments), 'fetch-comments'),
            bulkFetchComments: withFedops(bulkFetchComments(httpClient), 'bulk-fetch-comments'),
            fetchCommentThread: withFedops(fetchCommentThread(httpClient), 'fetch-comments-thread'),
            getBulkComments: withFedops(getBulkComments(httpClient), 'query-comments'),
            fetchCommentHistory: withFedops(fetchCommentHistory(httpClient), 'fetch-comment-history'),
        };
        const crudCalls = {
            createComment: withFedops(createComment(httpClient, persistDraft), 'create-comment'),
            updateComment: withFedops(updateComment(httpClient, persistDraft), 'update-comment'),
            deleteComment: withFedops(deleteComment(httpClient), 'delete-comment'),
            markComment: withFedops(markComment(httpClient), 'mark-comment'),
            unmarkComment: withFedops(unmarkComment(httpClient), 'unmark-comment'),
            addReaction: withFedops(addReaction(httpClient), 'add-reaction'),
            removeReaction: withFedops(removeReaction(httpClient), 'remove-reactions'),
            reportComment: withFedops(reportComment(httpClient), 'report-comment'),
        };
        return {
            ...withDevTools(crudCalls, getCrudDevState),
            ...withDevTools(fetchCalls, getFetchDevState),
        };
    })();
    return wrapServiceWithResult(service, {
        customMatchError: {
            createComment: (e) => {
                if (e instanceof Error) {
                    return { error: getCommentCreateError(e), baseError: e };
                }
                const unknownError = normalizeUnknownExceptionToError(e);
                return {
                    error: { type: 'TRANSLATION', key: 'toast.error-generic' },
                    baseError: unknownError,
                };
            },
        },
        errorEffect: onError,
    });
}
const getBulkComments = (httpClient) => async (payload) => {
    const { data } = await httpClient.request(serverGetBulkComments({ ...payload, useRichContent: true }));
    return fromServerGetBulkComments(data);
};
const fetchCommentThread = (httpClient) => async (payload) => {
    const { data } = await httpClient.request(serverFetchCommentThread({ ...payload, useRichContent: true }));
    return fromServerFetchCommentThread(data);
};
const fetchComments = (httpClient, mockFn) => async (payload) => {
    const response = mockFn
        ? await mockFn(payload)
        : (await httpClient.request(serverFetchComments({ ...payload, useRichContent: true }))).data;
    return fromServerFetchComments(response);
};
const bulkFetchComments = (httpClient) => async (payload) => {
    const { data } = await httpClient.request(serverBulkFetchComments({ ...payload, useRichContent: true }));
    assertFields(data, ['responses'], 'apiBulkListCommentsByResource response');
    return fromServerBulkFetchComments(data.responses);
};
const createComment = (httpClient, persistDraft) => async ({ comment, createVote, rating }) => {
    const { data } = await httpClient.request(serverCreateComment({
        request: { comment: toServerComment(comment) },
        ratingAction: rating ? toServerRatingAction(rating) : undefined,
        createVote,
        useRichContent: true,
        persistDraft,
    }));
    assertFields(data, ['response'], 'apiCreateComment response');
    assertFields(data.response, ['comment'], 'apiCreateComment response');
    return {
        comment: fromServerComment(data.response.comment),
        vote: data.vote ? fromServerVote(data.vote) : undefined,
        rating: data.resourceRating?.rating ?? undefined,
    };
};
const updateComment = (httpClient, persistDraft) => async ({ comment, rating }) => {
    const { data } = await httpClient.request(serverUpdateComment({
        request: { comment: comment ? toServerComment(comment) : undefined },
        ratingAction: rating ? toServerRatingAction(rating) : undefined,
        useRichContent: true,
        persistDraft,
    }));
    assertFields(data, ['response'], 'apiUpdateComment response');
    assertFields(data.response, ['comment'], 'apiUpdateComment response');
    return {
        comment: fromServerComment(data.response.comment),
        rating: data.resourceRating?.rating ?? undefined,
    };
};
const deleteComment = (httpClient) => async ({ commentId, cascade }) => {
    await httpClient.request(serverDeleteComment({ request: { commentId, cascade } }));
};
const markComment = (httpClient) => async (payload) => {
    const { data } = await httpClient.request(serverMarkComment({ request: payload }));
    return {
        isMarked: data.response?.comment?.marked,
        revision: data.response?.comment?.revision ?? undefined,
    };
};
const unmarkComment = (httpClient) => async (payload) => {
    const { data } = await httpClient.request(serverUnmarkComment({ request: payload }));
    return {
        isMarked: data.response?.comment?.marked,
        revision: data.response?.comment?.revision ?? undefined,
    };
};
const addReaction = (httpClient) => async (payload) => {
    await httpClient.request(serverAddReaction(payload));
};
const removeReaction = (httpClient) => async (payload) => {
    await httpClient.request(serverRemoveReaction(payload));
};
const reportComment = (httpClient) => async (payload) => {
    await httpClient.post(`/_api/comments-writer/v1/comments/${payload.commentId}/report`, {});
};
const fetchCommentHistory = (httpClient) => async ({ commentId, cursorPaging }) => {
    const { data } = await httpClient.request(serverFetchCommentHistory({
        request: {
            commentId,
            cursorPaging,
            sort: { fieldName: 'createdDate', order: SortOrder.DESC },
        },
    }));
    return fromServerFetchCommentHistory(data);
};
//# sourceMappingURL=create-comments-service.js.map